import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import { SEO_TRADES, TRADE_GROUPS, MONEY_TRADES } from '@/data/seo/trades';
import { COUNTIES, CITIES, citiesInCounty } from '@/data/seo/locations';
import { MONEY_PAGES, statewideMoneyPages } from '@/data/seo/moneyPages';

import { RESOURCES, RESOURCE_TOPICS } from '@/data/seo/resources';
import {
  breadcrumbSchema, faqSchema, itemListSchema, platformFaqs, locationFaqs,
} from '@/lib/seoSchema';

import {
  Breadcrumbs, PageHero, AnswerBlock, FaqSection, LinkGrid, CtaBand, PageWrap, Section,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';
import DirectorySearch from '@/components/seo/DirectorySearch';
import { STATES, stateLabel, statePath } from '@/data/seo/states';

/* ------------------------------------------------------------------ */
/* 1. TRADE DIRECTORY — /trades                                        */
/* ------------------------------------------------------------------ */
export const TradesHub: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const all = SEO_TRADES.map((x) => ({ name: lang === 'es' ? x.es : x.en, url: path(`/trades/${x.slug}`) }));
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Todos los Oficios de Construcción' : 'All Construction Trades'}
        description={
          lang === 'es'
            ? `Directorio de ${SEO_TRADES.length} oficios de construcción en Estados Unidos — techado, concreto, electricidad, plomería, HVAC y más.`
            : `Directory of ${SEO_TRADES.length} construction trades across the United States — roofing, concrete, electrical, plumbing, HVAC and more.`
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Trades', url: `/${lang}/trades` }]),
          itemListSchema('Construction trades', all),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={t('seo.tradeEyebrow')}
        title={lang === 'es' ? 'Directorio de Oficios de Construcción' : 'Construction Trade Directory'}
        intro={t('seo.tradesHubIntro', { n: SEO_TRADES.length })}
      />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.tradesTitle') }]} />
        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        {TRADE_GROUPS.map((g) => (
          <LinkGrid
            key={g.key}
            title={lang === 'es' ? g.es : g.en}
            icon="trade"
            links={SEO_TRADES.filter((x) => x.group === g.key).map((x) => ({
              label: lang === 'es' ? x.es : x.en,
              to: path(`/trades/${x.slug}`),
            }))}
          />
        ))}
        <FaqSection faqs={faqs} />
        <CtaBand />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 2. CALIFORNIA HUB — /california                                     */
/* ------------------------------------------------------------------ */
export const CaliforniaHub: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = locationFaqs('California', 'county', lang);
  const counties = COUNTIES.map((x) => ({ label: `${x.name} County`, to: path(`/california/counties/${x.slug}`) }));
  const cities = CITIES.slice(0, 60).map((x) => ({ label: `${x.name}, CA`, to: path(`/california/${x.slug}`) }));
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Construcción en California — Condados y Ciudades' : 'California Construction Projects by County & City'}
        description={
          lang === 'es'
            ? `Explore proyectos de construcción en los ${COUNTIES.length} condados de California y más de ${CITIES.length} ciudades.`
            : `Browse construction projects across all ${COUNTIES.length} California counties and ${CITIES.length}+ cities.`
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'California', url: `/${lang}/california` }]),
          itemListSchema('California counties', counties.map((c) => ({ name: c.label, url: c.to }))),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={t('seo.cityEyebrow')}
        title={lang === 'es' ? 'Construcción en California' : 'California Construction Marketplace'}
        intro={t('seo.caHubIntro', { counties: COUNTIES.length, cities: CITIES.length })}
      />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: 'California' }]} />
        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        <LiveProjects state="CA" heading={t('seo.recentProjects', { place: 'California' })} />
        <LinkGrid title={t('seo.allCounties')} links={counties} icon="map" />
        <LinkGrid title={t('seo.topCities')} links={cities} icon="map" />
        <LinkGrid
          title={t('seo.allTrades')}
          icon="trade"
          links={SEO_TRADES.slice(0, 24).map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }))}
        />
        <FaqSection faqs={faqs} />
        <CtaBand />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 3. LEADS HUB — /leads  (Buy Leads)                                  */
/* ------------------------------------------------------------------ */
export const LeadsHub: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const pages = MONEY_PAGES.map((m) => ({ label: `${m.trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));
  return (
    <>
      <Seo
        title={lang === 'es' ? `Comprar Leads de Construcción — ${money(LEAD_PRICE)} por lead` : `Buy Construction Leads — flat ${money(LEAD_PRICE)} per lead`}
        description={
          lang === 'es'
            ? `Leads de construcción verificados en todo Estados Unidos por ${money(LEAD_PRICE)}. Techado, concreto, electricidad, plomería, HVAC y más.`
            : `Verified construction leads across the United States for a flat ${money(LEAD_PRICE)}. Roofing, concrete, electrical, plumbing, HVAC and more.`
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Construction Leads', url: `/${lang}/leads` }]),
          itemListSchema('Construction lead pages', pages.map((p) => ({ name: p.label, url: p.to }))),
          faqSchema(faqs),
        ]}

      />
      <PageHero
        eyebrow={t('seo.moneyEyebrow')}
        title={lang === 'es' ? 'Comprar Leads de Construcción' : 'Buy Construction Leads'}
        intro={t('seo.leadsHubIntro', { price: money(LEAD_PRICE), n: MONEY_PAGES.length })}
      >
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaSubBtn')}
        </Link>
        <Link to={path('/pricing')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('nav.pricing')}
        </Link>
      </PageHero>
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.leadsHub') }]} />
        <AnswerBlock question={faqs[1].q} answer={faqs[1].a} />

        {/* Statewide lead pages in every state + DC (e.g. /leads/roofing-leads-texas). */}
        <section className="mt-10">
          <h2 className="text-lg font-black text-slate-900 sm:text-xl">
            {lang === 'es' ? 'Leads por estado — los 50 estados y D.C.' : 'Statewide leads — all 50 states + D.C.'}
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STATES.map((s) => (
              <div key={s.code} className="rounded-xl border border-slate-200 bg-white p-4">
                <Link to={path(statePath(s))} className="text-sm font-black text-slate-900 hover:text-orange-600">
                  {stateLabel(s, lang)}
                </Link>
                <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {statewideMoneyPages(s.code).map((m) => (
                    <li key={m.slug}>
                      <Link to={path(`/leads/${m.slug}`)} className="text-xs text-slate-600 hover:text-orange-600 hover:underline">
                        {lang === 'es' ? m.trade.es : m.trade.en}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* California metro + county inventory, grouped by trade. */}
        {MONEY_TRADES.map((tr) => (
          <LinkGrid
            key={tr.slug}
            title={lang === 'es' ? `Leads de ${tr.es} en California` : `${tr.en} Leads in California`}
            icon="map"
            links={MONEY_PAGES.filter((m) => m.trade.slug === tr.slug && m.stateCode === 'CA').map((m) => ({
              label: `${tr.en} Leads ${m.place}`,
              to: path(`/leads/${m.slug}`),
            }))}
          />
        ))}

        <FaqSection faqs={faqs} />
        <CtaBand />

      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 4. RESOURCE CENTER — /resources  (also serves /blog, /help)         */
/* ------------------------------------------------------------------ */
export const ResourcesHub: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const all = RESOURCES.map((r) => ({ name: lang === 'es' ? r.titleEs : r.title, url: path(`/resources/${r.slug}`) }));
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Centro de Recursos de Construcción' : 'Construction Resource Center'}
        description={
          lang === 'es'
            ? `${RESOURCES.length} guías para contratistas: licitación, estimación, licencias CSLB, contratos, seguridad y generación de leads.`
            : `${RESOURCES.length} contractor guides covering bidding, estimating, CSLB licensing, contracts, safety and lead generation.`
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Resource Center', url: `/${lang}/resources` }]),
          itemListSchema('Contractor guides', all),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={t('seo.resourceEyebrow')}
        title={lang === 'es' ? 'Centro de Recursos' : 'Resource Center'}
        intro={t('seo.resourcesHubIntro', { n: RESOURCES.length })}
      />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.resourcesTitle') }]} />
        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        {RESOURCE_TOPICS.map((topic) => (
          <LinkGrid
            key={topic.key}
            title={lang === 'es' ? topic.es : topic.en}
            columns={2}
            links={RESOURCES.filter((r) => r.topic === topic.key).map((r) => ({
              label: lang === 'es' ? r.titleEs : r.title,
              to: path(`/resources/${r.slug}`),
            }))}
          />
        ))}
        <FaqSection faqs={faqs} />
        <CtaBand />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 5. PROJECT DIRECTORY — /project-directory                           */
/* ------------------------------------------------------------------ */
export const ProjectDirectory: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Directorio de Proyectos de Construcción' : 'Construction Project Directory'}
        description={
          lang === 'es'
            ? 'Directorio de proyectos de construcción en Estados Unidos, organizado por oficio y estado, con páginas detalladas por condado y ciudad en California.'
            : 'Directory of construction projects across the United States, organised by trade and state, with county and city detail pages for California.'
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Project Directory', url: `/${lang}/project-directory` }]),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={t('seo.directoryEyebrow')}
        title={lang === 'es' ? 'Directorio de Proyectos' : 'Project Directory'}
        intro={t('seo.projectDirIntro')}
      />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.projectDirTitle') }]} />
        <DirectorySearch />
        <LiveProjects heading={lang === 'es' ? 'Proyectos recientes en EE. UU.' : 'Recent projects across the U.S.'} limit={9} />
        <LinkGrid
          title={t('seo.byTrade')}
          icon="trade"
          links={SEO_TRADES.map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }))}
        />
        <LinkGrid
          title={lang === 'es' ? 'Explorar por estado' : 'Browse by state'}
          icon="map"
          links={STATES.map((s) => ({ label: stateLabel(s, lang), to: path(statePath(s)) }))}
        />
        <LinkGrid
          title={lang === 'es' ? 'California por condado' : 'California by county'}
          icon="map"
          links={COUNTIES.map((x) => ({ label: `${x.name} County`, to: path(`/california/counties/${x.slug}`) }))}
        />
        <FaqSection faqs={faqs} />
        <CtaBand variant="post" />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 6. CONTRACTOR DIRECTORY — /contractor-directory                     */
/* ------------------------------------------------------------------ */
export const ContractorDirectory: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Directorio de Contratistas en Estados Unidos' : 'U.S. Contractor Directory'}
        description={
          lang === 'es'
            ? 'Encuentre subcontratistas y contratistas generales por oficio y ubicación en todo Estados Unidos.'
            : 'Find subcontractors and general contractors by trade and location across the United States.'
        }
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Contractor Directory', url: `/${lang}/contractor-directory` }]),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={t('seo.directoryEyebrow')}
        title={lang === 'es' ? 'Directorio de Contratistas' : 'Contractor Directory'}
        intro={t('seo.contractorDirIntro')}
      />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.contractorDirTitle') }]} />
        <DirectorySearch />
        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        <Section title={t('seo.privacyNoteTitle')}>
          <p>{t('seo.privacyNoteBody')}</p>
        </Section>
        <LinkGrid
          title={lang === 'es' ? 'Contratistas por estado' : 'Contractors by state'}
          icon="map"
          links={STATES.map((s) => ({ label: stateLabel(s, lang), to: path(statePath(s)) }))}
        />
        {COUNTIES.slice(0, 12).map((cty) => (
          <LinkGrid
            key={cty.slug}
            title={`${cty.name} County, CA`}
            icon="map"
            links={citiesInCounty(cty.slug).slice(0, 12).map((c) => ({ label: `${c.name}, CA`, to: path(`/california/${c.slug}`) }))}
          />
        ))}
        <FaqSection faqs={faqs} />
        <CtaBand />
      </PageWrap>
    </>
  );
};
